import React, { useState, useEffect } from 'react';
import api from '../api/client';
import './kits/KitPopup.css';
import './PlanPopup.css';

const PERIODS = [0, 7, 14, 30, 90, 180, 365];
const LB_TO_G = 453.592;

const CoverageTable = ({ title, rows, seeds }) => (
  <div className="plan-popup__table-block">
    <h5>{title}</h5>
    <table className="plan-popup__table">
      <thead>
        <tr>
          <th>Period (days)</th>
          <th>Stock</th>
          <th>Demand</th>
          {seeds ? (
            <>
              <th>Seeds Required (lb)</th>
              <th>Seeds Remaining (lb)</th>
            </>
          ) : (
            <th>Remaining Stock</th>
          )}
          <th>Covers</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((item, index) => (
          <tr key={index}>
            <td>{item.period}</td>
            <td>{typeof item.stock === 'number' ? item.stock.toLocaleString() : item.stock}</td>
            <td>{item.demand.toLocaleString()}</td>
            {seeds ? (
              <>
                <td>{item.seedsRequiredLb}</td>
                <td>{item.seedsRemainingLb}</td>
              </>
            ) : (
              <td>{item.remainingStock}</td>
            )}
            <td className={item.covers ? 'plan-popup__yes' : 'plan-popup__no'}>{item.covers ? 'Yes' : 'No'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Pager = ({ page, totalPages, setPage }) => (
  <div className="plan-popup__pager">
    <button type="button" onClick={() => setPage((prev) => Math.max(prev - 1, 1))} disabled={page === 1}>Previous</button>
    <span>Page {page} of {totalPages}</span>
    <button type="button" onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))} disabled={page === totalPages}>Next</button>
  </div>
);

const PlanPopup = ({ name, date, dateStr, sku, stock, demands, existingQty, existingUseSubstitute, id, plannedProduction, onSave, onDelete, onClose }) => {
  const [qty, setQty] = useState(existingQty || '');
  // Дата хранится строкой 'YYYY-MM-DD' — без Date/toISOString, чтобы не было сдвига по таймзоне
  const [selectedDate, setSelectedDate] = useState(dateStr);
  const [inventoryData, setInventoryData] = useState({
    canProduce: 0,
    bagsAvailable: 0,
    seedsAvailable: 0,
    gperpacket: 0,
    substitutes: [],
    canProduceWithSubstitutes: 0
  });
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  // План с заменой семян: в Max входит остаток взаимозаменяемых семян (General, Seeds Catalog)
  const [useSubstitute, setUseSubstitute] = useState(!!existingUseSubstitute);
  const [error, setError] = useState('');
  const [depletionInfo, setDepletionInfo] = useState('');
  const [showProductionLog, setShowProductionLog] = useState(false);
  const [productionLog, setProductionLog] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [logError, setLogError] = useState('');
  const [showInventoryLog, setShowInventoryLog] = useState(false);
  const [inventoryLog, setInventoryLog] = useState([]);
  const [inventoryLogCurrentPage, setInventoryLogCurrentPage] = useState(1);
  const [inventoryLogTotalPages, setInventoryLogTotalPages] = useState(1);
  const [inventoryLogLoading, setInventoryLogLoading] = useState(false);
  const [inventoryLogError, setInventoryLogError] = useState('');

  useEffect(() => {
    const fetchInventoryData = async () => {
      try {
        const response = await api.get('/api/get_can_produce_details', { params: { sku, withSubstitutes: 1 } });
        setInventoryData({
          canProduce: Number(response.data.canProduce) || 0,
          bagsAvailable: Number(response.data.bagsAvailable) || 0,
          seedsAvailable: Number(response.data.seedsAvailable) || 0,
          gperpacket: Number(response.data.gperpacket) || 0,
          substitutes: response.data.substitutes || [],
          canProduceWithSubstitutes: Number(response.data.canProduceWithSubstitutes) || 0
        });
        setInventoryLoaded(true);
      } catch (err) {
        console.error(`Error fetching inventory for SKU ${sku}:`, err);
        setError('Failed to load inventory data.');
      }
    };
    fetchInventoryData();
  }, [sku]);

  const { substitutes } = inventoryData;
  const withSubstitute = useSubstitute && substitutes.length > 0;
  const substituteLb = substitutes.reduce((sum, s) => sum + (Number(s.seedsAvailable) || 0), 0);
  const canProduce = withSubstitute ? inventoryData.canProduceWithSubstitutes : inventoryData.canProduce;
  const seedsAvailable = inventoryData.seedsAvailable + (withSubstitute ? substituteLb : 0);
  const exceedsMax = inventoryLoaded && Number(qty) > canProduce;
  const exceedsMessage = `Planned quantity (${Number(qty).toLocaleString()}) exceeds Can Produce (${canProduce.toLocaleString()}).`;

  useEffect(() => {
    if (!inventoryLoaded) return;
    if (!qty || isNaN(qty) || Number(qty) < 0) {
      setDepletionInfo('');
      setError('');
      return;
    }

    const plannedQty = Number(qty);
    if (plannedQty > canProduce) {
      setError(exceedsMessage);
      setDepletionInfo('');
      return;
    }
    setError('');

    let remainingBags = inventoryData.bagsAvailable;
    const sortedPlans = Object.keys(plannedProduction)
      .filter(key => key.startsWith(`${sku}_`))
      .map(key => ({
        date: key.split('_')[1],
        qty: Number(plannedProduction[key]?.qty || 0)
      }))
      .sort((a, b) => a.date.localeCompare(b.date));

    let depletionDate = null;
    for (const plan of sortedPlans) {
      const planQty = plan.date === selectedDate ? plannedQty : plan.qty;
      remainingBags -= planQty;
      if (remainingBags <= 0 && !depletionDate) {
        depletionDate = plan.date;
      }
    }

    if (remainingBags <= 0 && depletionDate) {
      const [y, m, d] = depletionDate.split('-');
      setDepletionInfo(`Bags will run out on ${Number(m)}/${Number(d)}/${y}.`);
    } else if (remainingBags > 0) {
      setDepletionInfo('Bags will not run out within the planned period.');
    } else {
      setDepletionInfo('Bags are already depleted.');
    }
  }, [qty, inventoryData, inventoryLoaded, canProduce, plannedProduction, sku, selectedDate]);

  const fetchProductionLog = async (page = 1) => {
    setIsLoading(true);
    setLogError('');
    try {
      const response = await api.get('/api/sbproduction_dailyplanlog', {
        params: {
          page,
          limit: 10,
          sku: sku
        }
      });
      setProductionLog(response.data.data);
      setTotalPages(Math.ceil(response.data.total / 10));
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching production log:', err);
      setLogError('Failed to load production log.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInventoryLog = async (page = 1) => {
    setInventoryLogLoading(true);
    setInventoryLogError('');
    try {
      const response = await api.get('/api/smallbagsinventory_log', {
        params: {
          page,
          limit: 10,
          sku: sku
        }
      });
      setInventoryLog(response.data.data);
      setInventoryLogTotalPages(Math.ceil(response.data.total / 10));
      setInventoryLogCurrentPage(page);
    } catch (err) {
      console.error('Error fetching inventory log:', err);
      setInventoryLogError('Failed to load inventory log.');
    } finally {
      setInventoryLogLoading(false);
    }
  };

  useEffect(() => {
    if (showProductionLog) {
      fetchProductionLog(currentPage);
    }
  }, [showProductionLog, currentPage, sku]);

  useEffect(() => {
    if (showInventoryLog) {
      fetchInventoryLog(inventoryLogCurrentPage);
    }
  }, [showInventoryLog, inventoryLogCurrentPage, sku]);

  const handleSave = () => {
    if (!qty || isNaN(qty) || Number(qty) < 0) {
      setError('Please enter a valid quantity.');
      return;
    }
    if (Number(qty) > canProduce) {
      setError(exceedsMessage);
      return;
    }
    onSave(sku, selectedDate, qty, id, withSubstitute);
  };

  const totalAvailable = stock + canProduce;
  const totalCoverage = PERIODS.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const remainingStock = period === 0 ? totalAvailable : totalAvailable - demand;
    return {
      period,
      demand,
      covers: period === 0 ? true : totalAvailable >= demand,
      stock: index === 0 ? totalAvailable : '-',
      remainingStock: remainingStock.toLocaleString(),
    };
  });

  const bagsCoverage = PERIODS.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const remainingStock = period === 0 ? inventoryData.bagsAvailable : inventoryData.bagsAvailable - demand;
    return {
      period,
      demand,
      covers: period === 0 ? true : inventoryData.bagsAvailable >= demand,
      stock: index === 0 ? inventoryData.bagsAvailable : '-',
      remainingStock: remainingStock.toLocaleString(),
    };
  });

  const { gperpacket } = inventoryData;
  const packetsFromSeeds = seedsAvailable && gperpacket ? Math.floor(seedsAvailable * LB_TO_G / gperpacket) : 0;
  const seedsCoverage = PERIODS.map((period, index) => {
    const demand = period === 0 ? 0 : demands[index - 1] || 0;
    const seedsRequiredLb = gperpacket ? (demand * gperpacket / LB_TO_G).toFixed(2) : 'N/A';
    const seedsRemainingLb = period === 0 ? seedsAvailable.toFixed(2) : gperpacket ? (seedsAvailable - (demand * gperpacket / LB_TO_G)).toFixed(2) : 'N/A';
    return {
      period,
      demand,
      covers: period === 0 ? true : packetsFromSeeds >= demand,
      stock: index === 0 ? `${seedsAvailable.toFixed(2)} lb` : '-',
      seedsRequiredLb,
      seedsRemainingLb
    };
  });

  return (
    <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="kit-popup plan-popup" role="dialog" aria-modal="true" aria-label="Plan Production">
        <div className="kit-popup__header">
          <div>
            <div className="kit-popup__title">Plan Production</div>
            <div className="kit-popup__subtitle">{name} · {sku}</div>
          </div>
          <button className="kit-popup__close" type="button" onClick={onClose} aria-label="Close">
            <i className="fa-solid fa-xmark" aria-hidden="true"></i>
          </button>
        </div>

        <div className="plan-popup__body">
          <div className="plan-popup__fields">
            <label className="kit-field">
              <span className="kit-field__label">Date</span>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="kit-field__input"
              />
            </label>
            <label className="kit-field">
              <span className="kit-field__label">Planned Qty to Produce</span>
              <div className="plan-popup__qty">
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  min="0"
                  className="kit-field__input"
                  autoFocus
                />
                <span className={`plan-popup__max${exceedsMax ? ' plan-popup__max--over' : ''}`}>
                  Max: {inventoryLoaded ? canProduce.toLocaleString() : '…'}
                </span>
              </div>
            </label>
          </div>

          {substitutes.length > 0 && (
            <div className={`plan-popup__substitute${withSubstitute ? ' plan-popup__substitute--on' : ''}`}>
              <button
                type="button"
                role="switch"
                aria-checked={withSubstitute}
                className="plan-popup__switch"
                onClick={() => setUseSubstitute(v => !v)}
              >
                <span className="plan-popup__switch-track"><span className="plan-popup__switch-thumb" /></span>
                <span>
                  <span className="plan-popup__switch-title">Use substitute seeds</span>
                  <span className="plan-popup__switch-hint">
                    These seeds are marked as interchangeable. When switched on, their stock is added to Max.
                  </span>
                </span>
              </button>
              <ul className="plan-popup__substitute-list">
                {substitutes.map(s => (
                  <li key={s.seedsID}>
                    <i className="fa-solid fa-right-left" aria-hidden="true"></i>
                    <span className="plan-popup__substitute-name">{s.name}</span>
                    <span>{Number(s.seedsAvailable).toFixed(2)} lb</span>
                    <span className="plan-popup__substitute-packets">
                      {s.seedsAvailable > 0
                        ? (s.packets != null ? `enough for ${s.packets.toLocaleString()} packets` : '')
                        : 'no stock'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {inventoryLoaded && substitutes.length === 0 && inventoryData.seedsAvailable <= 0 && (
            <div className="plan-popup__note">
              No seeds in stock for this packet. To plan it with another seed, mark that seed as interchangeable
              in General, Seeds Catalog.
            </div>
          )}

          <div className="plan-popup__stats">
            <div className="plan-popup__stat plan-popup__stat--main">
              <span className="plan-popup__stat-label">Can Produce</span>
              <span className="plan-popup__stat-value">{canProduce.toLocaleString()}</span>
              <span className="plan-popup__stat-sub">{withSubstitute ? 'packets, with substitute seeds' : 'packets'}</span>
            </div>
            <div className="plan-popup__stat">
              <span className="plan-popup__stat-label">Empty Packets</span>
              <span className="plan-popup__stat-value">{inventoryData.bagsAvailable.toLocaleString()}</span>
              <span className="plan-popup__stat-sub">in stock</span>
            </div>
            <div className="plan-popup__stat">
              <span className="plan-popup__stat-label">Seeds Available</span>
              <span className="plan-popup__stat-value">{seedsAvailable.toFixed(2)} lb</span>
              <span className="plan-popup__stat-sub">
                {withSubstitute
                  ? `own ${inventoryData.seedsAvailable.toFixed(2)} lb + substitute ${substituteLb.toFixed(2)} lb`
                  : 'own seeds'}
              </span>
            </div>
          </div>

          {depletionInfo && <div className="plan-popup__info"><strong>Depletion Info:</strong> {depletionInfo}</div>}
          {error && <div className="plan-popup__error" role="alert">{error}</div>}

          <div className="plan-popup__details">
            <h4>Details</h4>
            <CoverageTable title="Total (Stock + CBP)" rows={totalCoverage} />
            <div className="plan-popup__table-row">
              <CoverageTable title="Empty Packets" rows={bagsCoverage} />
              <CoverageTable title={withSubstitute ? 'Seeds (with substitute)' : 'Seeds'} rows={seedsCoverage} seeds />
            </div>

            <div className="plan-popup__log">
              <h4>Calculation Log</h4>
              <p>Stock Packets with Seeds: {stock.toLocaleString()} packets</p>
              <p>CBP: {canProduce.toLocaleString()} packets</p>
              <p>Total Available (Stock + CBP): {totalAvailable.toLocaleString()} packets</p>
              <h4>CBP Details</h4>
              <p>Empty Packets Available: {inventoryData.bagsAvailable.toLocaleString()}</p>
              <p>Seeds Available LB: {inventoryData.seedsAvailable.toFixed(2)} lb</p>
              {withSubstitute && (
                <p>Substitute Seeds Available LB: {substituteLb.toFixed(2)} lb ({substitutes.map(s => s.name).join(', ')})</p>
              )}
              <p>Seeds Available G: {(seedsAvailable * LB_TO_G).toFixed(2)} g</p>
              <p>Grams per Packet: {gperpacket ? gperpacket.toLocaleString() : 'N/A'} g</p>
              <p>Packets from Seeds: {seedsAvailable && gperpacket ? `(${seedsAvailable.toFixed(2)} lb × ${LB_TO_G} g/lb) ÷ ${gperpacket} g/packet = ${packetsFromSeeds.toLocaleString()} packets` : '0 packets'}</p>
            </div>

            <div className="plan-popup__log-buttons">
              <button type="button" className="plan-popup__btn" onClick={() => setShowProductionLog(v => !v)}>
                {showProductionLog ? 'Hide Production Log' : 'Show Production Log'}
              </button>
              <button type="button" className="plan-popup__btn" onClick={() => setShowInventoryLog(v => !v)}>
                {showInventoryLog ? 'Hide Inventory Log' : 'Show Inventory Log'}
              </button>
            </div>

            {showProductionLog && (
              <div className="plan-popup__table-block">
                <h5>Production Log</h5>
                {isLoading ? (
                  <p className="plan-popup__muted">Loading...</p>
                ) : logError ? (
                  <p className="plan-popup__error">{logError}</p>
                ) : (
                  <>
                    <table className="plan-popup__table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Date</th>
                          <th>SKU</th>
                          <th>Name</th>
                          <th>Employees</th>
                          <th>Machine</th>
                          <th>QTY</th>
                          <th>Damage</th>
                          <th>Seeds Used</th>
                          <th>Time Spend</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productionLog.map((row) => (
                          <tr key={row.recID}>
                            <td>{row.recID}</td>
                            <td>{row.prodDate}</td>
                            <td>{row.sku}</td>
                            <td>{row.packName}</td>
                            <td>{row.employees}</td>
                            <td>{row.machinenumber}</td>
                            <td>{row.qty}</td>
                            <td>{row.damage}</td>
                            <td>{row.usedSeeds}</td>
                            <td>{row.hoursTook}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Pager page={currentPage} totalPages={totalPages} setPage={setCurrentPage} />
                  </>
                )}
              </div>
            )}

            {showInventoryLog && (
              <div className="plan-popup__table-block">
                <h5>Inventory Log</h5>
                {inventoryLogLoading ? (
                  <p className="plan-popup__muted">Loading...</p>
                ) : inventoryLogError ? (
                  <p className="plan-popup__error">{inventoryLogError}</p>
                ) : (
                  <>
                    <table className="plan-popup__table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>SKU</th>
                          <th>Old QTY</th>
                          <th>New QTY</th>
                          <th>Changed By</th>
                          <th>Date</th>
                          <th>Source</th>
                          <th>Production Record ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryLog.map((row) => (
                          <tr key={row.logID}>
                            <td>{row.logID}</td>
                            <td>{row.sku}</td>
                            <td>{row.old_qty}</td>
                            <td>{row.new_qty}</td>
                            <td>{row.changed_by}</td>
                            <td>{new Date(row.change_datetime).toLocaleString()}</td>
                            <td>{row.change_source}</td>
                            <td>{row.recID}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <Pager page={inventoryLogCurrentPage} totalPages={inventoryLogTotalPages} setPage={setInventoryLogCurrentPage} />
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="kit-popup__footer plan-popup__footer">
          {id && onDelete ? (
            <button
              type="button"
              className="plan-popup__btn plan-popup__btn--danger"
              onClick={() => {
                if (window.confirm('Delete this planned production?')) onDelete(id);
              }}
            >
              Delete
            </button>
          ) : <span />}
          <div className="plan-popup__footer-right">
            <button type="button" className="plan-popup__btn" onClick={onClose}>Cancel</button>
            <button type="button" className="kit-popup__submit" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanPopup;
